import React, { useEffect, useLayoutEffect } from "react";

// Style
import "./MoleculeProduct.scss";

// Link
import { useNavigate, useParams } from "react-router-dom";

// Lib
import { sanitize } from "dompurify";
import { shallowEqual } from "react-redux";
import { convertDataPrices, decodeHtmlEntity } from "lib/helpers";
import { useAppDispatch, useAppSelector } from "lib/hooks/useDispatch";

// Cookies
import Cookies from "js-cookie";
import { amplitudeInstance, events } from "constans/cookie";

// Actions
import * as actionCreators from "./reducer";

// Components
import NoData from "components/NoData";
import Status from "./components/Status";
import Tabs from "components/UiKit/Tabs";
import HelmetCeo from 'components/HelmetCeo';
import PricesList from "./components/PricesList";
import DrugImpurity from "components/DrugImpurity";
import Description from "./components/Description";
import TotalAmount from "./components/TotalAmount";
import Accordion from "components/UiKit/Accordion";
import InfoText from "components/InfoText/InfoText";
import NavigationBack from "components/NavigationBack";
import TableMass from "./components/TableSolubilities";
import ChemicalProbes from "./components/ChemicalProbs";
import RelatedCompounds from './components/RelatedCompounds';
import PharmacopoeiaItems from "components/PharmacopoeiaItems";
import ProductDatasheets from "./components/ProductDatasheets";
import LicensingInformation from "./components/LicensingInformation";
import DilutionCalculator from "./components/Calculators/DilutionCalculator";
import MolarityCalculator from "./components/Calculators/MolarityCalculator";
import FormulationCalculator from "./components/Calculators/FormulationCalculator";

// Constants
import { configMainDataNotAuth } from "constans/pharmacopoeia";
import { tableSolubilitiesHeader } from "constans/molecule-product";

// Classnames
import clsx from "clsx";

// Type
type PropsMap = {
  text?: string;
  title: string;
  isOpen: boolean;
  className?: string;
  children?: React.ReactNode,
};

interface Price {
  supplier: string;
  prices: any[];
}

export const MoleculeProduct: React.FC = () => {
  const urlParam = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    auth,
    typePrice,
    currentPrice,

    data,
    mainActivity,
    chemicalProbes,
    inactiveControl,
    dataSolubilities
  } = useAppSelector(
    (store) => {
      return {
        auth: store.settings.user.authorized,

        typePrice: store.settings.typePrice,
        currentPrice: store.settings.currentPrice,

        data: store.moleculeProduct.data,
        mainActivity: store.moleculeProduct.mainActivity,
        chemicalProbes: store.moleculeProduct.chemicalProbes,
        inactiveControl: store.moleculeProduct.inactiveControl,
        dataSolubilities: store.moleculeProduct.dataSolubilities,
      }
    },
    shallowEqual
  );

  const { id } = urlParam;
  const supIds = data.supIds;
  const licenseAgreement = data.licenseAgreement;
  const findPrices = data?.prices.find((item: Price) => item.supplier === typePrice) || {
    supplier: "EBC",
    prices: []
  };
  const convertedPrices = convertDataPrices(findPrices?.prices || []);
  const optionsRadioTypePrices = data?.prices.map((item: any) => ({
    label: item.supplier,
    value: item.supplier
  }));

  // Scroll to top after navigating
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [navigate]);

  useEffect(() => {
    if (id) {
      const isCookie = Cookies.get("cookieName");

      dispatch(actionCreators.getConvertAnyIdToEbcId(id));

      if (isCookie) {
        amplitudeInstance.logEvent(events.molecule_product, { id: id });
      }
    }
  }, [id]);

  useEffect(() => {
    return () => {
      dispatch(actionCreators.handlerClearState());
    };
  }, []);

  const properties = <div className="flex flex-ai-start">
    <div className="mr10">
      <span className="mr10">cLogP:</span>
      {data?.descriptor?.logP ? data.descriptor.logP : "-"}
    </div>
    <div className="mr10">
      <span className="mr10">MW:</span>
      {data?.descriptor?.molecularWeight ? data.descriptor.molecularWeight : "-"}
    </div>
    {!!data?.pharmacopoeia?.length && <div className="mr10">
      <span className="mr10">Pharmacopoeia:</span>
      <PharmacopoeiaItems isComma data={data.pharmacopoeia} className="molecule-product__tooltip" />
    </div>}
  </div>;

  const getTitle = (data: actionCreators.Supplier[]) => {
    const countIds = data?.length;
    return data.map((item: actionCreators.Supplier, i: number) => {
      return (<div className={`flex flex-ai-center ${i === 0 ? 'ml10' : ''}`} key={`${id}_2-${i}`}>
        <span
          dangerouslySetInnerHTML={{ __html: sanitize(decodeHtmlEntity(item?.supId || "")) }}
        />
        {i < countIds - 1 && <p className="mr5 ml5">|</p>}
      </div>);
    });
  };

  const configAccordion = [
    {
      isOpen: true,
      title: "Synonyms",
      className: "molecule-product__synonyms",
      text: data?.synonyms ? data?.synonyms : "-"
    },
    {
      isOpen: !!dataSolubilities?.length,
      title: "Solubility",
      children: dataSolubilities.length ? <TableMass
        data={dataSolubilities}
        columns={tableSolubilitiesHeader}
      /> : <NoData text="No data available" className="table-solubilities__no-data" />
    },
    {
      isOpen: false,
      title: "Purity & Quality  Control",
      text: "The compound has purity validated by NMR and/or LCMS methods."
    }
  ];
  const configMainData = [
    {
      label: "Drug bank ID",
      value: data?.dbId ? data.dbId : ""
    },
    {
      label: "Targets",
      value: mainActivity?.mainTarAllPx ? mainActivity?.mainTarAllPx : ""
    },
    {
      label: "Target actions",
      value: mainActivity?.actionOnTargets ? mainActivity?.actionOnTargets : ""
    },
    {
      label: "Bio species",
      value: mainActivity?.bioSpecies ? mainActivity?.bioSpecies : ""
    },
    {
      label: "Activity values",
      value: mainActivity?.activityValAll ? mainActivity?.activityValAll : ""
    },
    {
      label: "References",
      value: mainActivity?.mainTarAllRefs ? mainActivity?.mainTarAllRefs : ""
    }
  ];

  const convertMainData = auth ?
    !!mainActivity?.mainTarAllRefs?.length
      ? configMainData
      : []
    : configMainDataNotAuth;

  const configDescription = [
    {
      value: properties,
      label: "Properties",
      className: "flex"
    },
    {
      label: "Name",
      value: data?.name ? data.name : "-"
    },
    {
      label: "Smiles",
      value: data.cxSmiles ? data.cxSmiles : "-"
    },
    ...convertMainData
  ];

  const configTabsCalculator = [
    { label: "Molarity Calculator", content: <MolarityCalculator />, url: "molarity" },
    { label: "Dilution Calculator", content: <DilutionCalculator />, url: "dilution" },
    { label: "Formulation Calculator", content: <FormulationCalculator />, url: "formulation" }
  ];
  const isPurity = !!data?.purity ? `${data.purity}%` : '';
  const dataCeo = [
    {
      name: 'robots',
      content: 'index,follow',
    },
    {
      name: 'keywords',
      content: `Id: ${data.id || ''}, CAS Number: ${data.cas || ''}, Purity: ${isPurity}, Smiles: ${data?.cxSmiles || ''}, Drug bank ID: ${data?.dbId || ''}, Targets: ${mainActivity?.mainTarAllPx || ''}, Target actions: ${mainActivity?.actionOnTargets || ''}, Bio species: ${mainActivity?.bioSpecies || ''}, Synonyms: ${data?.synonyms || ''}`,
    },
    {
      name: 'description',
      content: `${data?.iupacName || ''}`,
    }
  ];

  return (
    <>
      <HelmetCeo data={dataCeo} />
      <div className="molecule-product">
        <div className="wrapper relative">
          <NavigationBack label="Back" />
          <h1 className="molecule-product__title flex flex-ai-center">
            Product details: {getTitle(supIds || [])}
          </h1>

          <div className="flex flex-ai-start flex-direction-column-desktop">
            <div className="flex flex-direction-column molecule-product__left-block">
              <div className="molecule-product__description-top molecule-product__description-top-bg relative">
                <Description {...data} />
                <div className="molecule-product__div-bottom relative" />
              </div>
              <div className="molecule-product__description-bottom relative mb5">
                <div className="molecule-product__div-top" />
                <div className="molecule-product__description-bottom-bg">
                  {configDescription.map((item: any, i: number) => {
                    const cnValue = clsx(
                      "molecule-product__text-p",
                      item.className ? item.className : ""
                    );
                    return (
                      <div
                        key={`${item.label}_${i}`}
                        className="molecule-product__text flex flex-ai-start flex-direction-column-desktop"
                      >
                        <p className="molecule-product__text-span">{item.label}</p>
                        {item?.label === "Properties" ?
                          item.value
                          :
                          <p
                            className={cnValue}
                            dangerouslySetInnerHTML={{ __html: sanitize(decodeHtmlEntity(item?.value || "-") || "-") }}
                          />}
                      </div>
                    );
                  })}

                  <DrugImpurity
                    drugForImp={data?.drugsImpurities?.drugForImp}
                    impForDrug={data?.drugsImpurities?.impForDrug}
                    className="molecule-product__drug-impurity-line"
                  />
                </div>
              </div>

              {licenseAgreement && <LicensingInformation className="molecule-product__accordion" />}

              {chemicalProbes !== null && <ChemicalProbes
                data={chemicalProbes}
                inactiveControl={inactiveControl}
              />}

              <ProductDatasheets />

              <div className="molecule-product__accordion display-block-web mb5">
                {configAccordion.map((item: PropsMap) => {
                  const classNameContent = `molecule-product__accordion-width ${item?.className ? item.className : ""} ${item?.children ? "molecule-product__solubilities" : ""}`;
                  return <Accordion
                    key={item.title}
                    title={item.title}
                    isOpen={item.isOpen}
                    classNameContent={classNameContent}
                    className="molecule-product__border-accordion"
                  >
                    {item?.children ? item?.children :
                      <p dangerouslySetInnerHTML={{ __html: sanitize(item?.text || "") }} />}
                  </Accordion>;
                })}
              </div>

              <div className="molecule-product__accordion display-block-web">
                <Tabs
                  type="secondary"
                  defaultScreen="molarity"
                  tabs={configTabsCalculator}
                />
              </div>
            </div>

            <div className="molecule-product__right-block">
              <PricesList
                typePrice={typePrice}
                currentPrice={currentPrice}
                prices={convertedPrices || []}
                optionsRadioTypePrices={optionsRadioTypePrices}
              />

              <div className="molecule-product__bg-status">
                <TotalAmount />
                <Status
                  prices={convertedPrices || []}
                  onStock={data.onStock || false}
                />
              </div>

              <div className="display-flex-table flex-direction-column-desktop">
                {configAccordion.map((item: PropsMap) => {
                  const classNameContent = `molecule-product__accordion-width ${item?.className ? item.className : ""} ${item?.children ? "molecule-product__solubilities" : ""}`;
                  return <Accordion
                    key={item.title}
                    title={item.title}
                    isOpen={item.isOpen}
                    classNameContent={classNameContent}
                  >
                    {item?.children ? item?.children :
                      <p dangerouslySetInnerHTML={{ __html: sanitize(item?.text || "") }} />}
                  </Accordion>;
                })}
              </div>

              <InfoText
                isHiddeText
                className="mt5"
                title="Target activity features"
                classNameTitle="molecule-product__title-info"
                subTitle="It should be emphasized that the product may be active against a larger number of targets than shown on the card.  The information represented here refers to the targets with the largest value of pX or the targets with ΔpX less than 1.5 from the largest pX value."
              />
            </div>
          </div>

          <RelatedCompounds />
        </div>
      </div>
    </>
  );
};

export default MoleculeProduct;
